const money = (cents, currency, locale) => {
  try { return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format((cents || 0) / 100); }
  catch { return ((cents || 0) / 100).toFixed(2); }
};

class ZoomEyewearProduct {
  constructor(root) {
    this.root = root;
    this.variants = JSON.parse(root.querySelector('[data-ze-variants]')?.textContent || '[]');
    this.currency = root.dataset.currency || 'USD';
    this.locale = root.dataset.locale || document.documentElement.lang || 'en';
    this.lowThreshold = Number(root.dataset.lowThreshold || 5);
    this.optionGroups = [...root.querySelectorAll('[data-ze-option-position]')];
    this.mediaItems = [...root.querySelectorAll('[data-ze-media]')];
    this.thumbs = [...root.querySelectorAll('[data-ze-thumb]')];
    this.currentIndex = Math.max(0, this.mediaItems.findIndex((item) => item.classList.contains('is-active')));
    this.bind();
    this.bindGallerySwipe();
    this.bindStickyBuy();
  }

  bind() {
    this.root.querySelectorAll('[data-ze-option-value]').forEach((button) => button.addEventListener('click', () => this.selectOption(button)));
    this.thumbs.forEach((button) => button.addEventListener('click', () => this.showMedia(button.dataset.zeThumb)));
    this.root.querySelector('[data-ze-gallery-prev]')?.addEventListener('click', () => this.stepMedia(-1));
    this.root.querySelector('[data-ze-gallery-next]')?.addEventListener('click', () => this.stepMedia(1));
    this.root.querySelector('[data-ze-qty-minus]')?.addEventListener('click', () => this.changeQty(-1));
    this.root.querySelector('[data-ze-qty-plus]')?.addEventListener('click', () => this.changeQty(1));
    this.root.querySelector('[data-ze-mobile-submit]')?.addEventListener('click', () => this.root.querySelector('.ze-product-form')?.requestSubmit());
    const dialog = this.root.querySelector('[data-ze-size-guide]');
    this.root.querySelectorAll('[data-ze-open-size-guide]').forEach((button) => button.addEventListener('click', () => dialog?.showModal()));
    this.root.querySelector('[data-ze-close-size-guide]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    const imageDialog = this.root.querySelector('[data-ze-image-zoom]');
    const zoomImage = imageDialog?.querySelector('[data-ze-image-zoom-img]');
    this.root.querySelectorAll('[data-ze-zoom-trigger]').forEach((button) => button.addEventListener('click', () => {
      if (!imageDialog || !zoomImage) return;
      zoomImage.src = button.dataset.zeZoomSrc || button.querySelector('img')?.currentSrc || '';
      zoomImage.alt = button.querySelector('img')?.alt || '';
      imageDialog.showModal();
    }));
    this.root.querySelector('[data-ze-close-image-zoom]')?.addEventListener('click', () => imageDialog?.close());
    imageDialog?.addEventListener('click', (event) => { if (event.target === imageDialog) imageDialog.close(); });
    this.root.querySelectorAll('.ze-product-accordions details').forEach((detail) => detail.addEventListener('toggle', () => {
      if (!detail.open) return;
      this.root.querySelectorAll('.ze-product-accordions details').forEach((other) => { if (other !== detail) other.open = false; });
    }));
  }


  bindGallerySwipe() {
    const stage = this.root.querySelector('.ze-gallery__stage');
    if (!stage) return;
    let startX = null;
    let startY = null;
    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      startX = event.clientX;
      startY = event.clientY;
    }, { passive: true });
    stage.addEventListener('pointerup', (event) => {
      if (startX === null || startY === null) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      startX = null;
      startY = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)) return;
      const rtl = document.documentElement.dir === 'rtl';
      const direction = dx < 0 ? 1 : -1;
      this.stepMedia(rtl ? -direction : direction);
    }, { passive: true });
    stage.addEventListener('pointercancel', () => { startX = null; startY = null; }, { passive: true });
  }

  bindStickyBuy() {
    const primaryBuy = this.root.querySelector('.ze-buy-row');
    const sticky = this.root.querySelector('[data-ze-mobile-buy]');
    if (!primaryBuy || !sticky || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const aboveViewport = entry.boundingClientRect.bottom <= 0;
      sticky.classList.toggle('is-visible', !entry.isIntersecting && aboveViewport);
    }, { threshold: 0 });
    observer.observe(primaryBuy);
  }

  selectedOptions() {
    return this.optionGroups.map((group) => group.querySelector('[data-ze-option-value].is-selected')?.dataset.zeOptionValue || '');
  }

  selectOption(button) {
    const group = button.closest('[data-ze-option-position]');
    group.querySelectorAll('[data-ze-option-value]').forEach((item) => {
      const selected = item === button;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    const options = this.selectedOptions();
    const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
    if (variant) this.updateVariant(variant);
  }

  updateVariant(variant) {
    this.root.querySelector('[data-ze-variant-input]').value = variant.id;
    const available = Boolean(variant.available);
    const currentPrice = money(variant.price, this.currency, this.locale);
    const comparePrice = variant.compare_at_price > variant.price ? money(variant.compare_at_price, this.currency, this.locale) : '';
    const discountPercent = variant.compare_at_price > variant.price ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
    this.root.querySelector('[data-ze-price-current]').textContent = currentPrice;
    const compare = this.root.querySelector('[data-ze-price-compare]');
    compare.textContent = comparePrice;
    compare.classList.toggle('is-hidden', !comparePrice);
    const discount = this.root.querySelector('[data-ze-discount]');
    if (discount) {
      const saveTemplate = this.root.dataset.saveTemplate || 'Save __PERCENT__%';
      discount.textContent = discountPercent ? saveTemplate.replace('__PERCENT__', discountPercent) : '';
      discount.classList.toggle('is-hidden', !discountPercent);
    }
    this.root.querySelectorAll('[data-ze-add-price],[data-ze-mobile-price]').forEach((el) => { el.textContent = currentPrice; });
    this.root.querySelectorAll('[data-ze-payment-variant-input]').forEach((el) => { el.value = variant.id; });
    const compareStack = this.root.querySelector('[data-ze-compare-stack]');
    if (compareStack) compareStack.classList.toggle('is-hidden', !comparePrice);
    const add = this.root.querySelector('[data-ze-add-button]');
    const mobile = this.root.querySelector('[data-ze-mobile-submit]');
    add.disabled = !available;
    if (mobile) mobile.disabled = !available;
    const availableLabel = this.root.dataset.addLabel || 'Add to cart';
    const soldLabel = this.root.dataset.soldLabel || 'Sold out';
    this.root.querySelectorAll('[data-ze-add-label],[data-ze-mobile-label]').forEach((el) => { el.textContent = available ? availableLabel : soldLabel; });
    const availability = this.root.querySelector('[data-ze-availability]');
    if (availability) {
      const managed = variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';
      const low = available && managed && Number(variant.inventory_quantity) > 0 && Number(variant.inventory_quantity) <= this.lowThreshold;
      availability.classList.toggle('ze-product__availability--low', low);
      if (!available) {
        availability.textContent = this.root.dataset.outLabel || soldLabel;
      } else if (low) {
        const template = this.root.dataset.lowStockTemplate || 'Only __COUNT__ left';
        availability.innerHTML = `<span class="ze-dot ze-dot--low"></span>${template.replace('__COUNT__', variant.inventory_quantity)}`;
      } else {
        availability.innerHTML = `<span class="ze-dot"></span>${this.root.dataset.readyLabel || 'Ready to ship'}`;
      }
    }
    const sku = this.root.querySelector('[data-ze-sku]');
    if (sku && variant.sku) sku.textContent = variant.sku;
    if (variant.featured_media_id) this.showMedia(String(variant.featured_media_id));
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  showMedia(id) {
    const index = this.mediaItems.findIndex((item) => item.dataset.zeMedia === String(id));
    if (index < 0) return;
    this.currentIndex = index;
    this.mediaItems.forEach((item, idx) => {
      const active = idx === index;
      item.classList.toggle('is-active', active);
      item.hidden = !active;
    });
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.zeThumb === String(id);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
    const counter = this.root.querySelector('[data-ze-gallery-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  stepMedia(direction) {
    if (!this.mediaItems.length) return;
    const next = (this.currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
    this.showMedia(this.mediaItems[next].dataset.zeMedia);
  }

  changeQty(delta) {
    const input = this.root.querySelector('[data-ze-qty]');
    if (!input) return;
    input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

const initZoomEyewear = (scope = document) => {
  scope.querySelectorAll('[data-ze-product]:not([data-ze-initialized])').forEach((root) => {
    root.dataset.zeInitialized = 'true';
    new ZoomEyewearProduct(root);
  });
  scope.querySelectorAll('[data-ze-recommendations]:not([data-ze-rec-initialized])').forEach((section) => {
    section.dataset.zeRecInitialized = 'true';
    initRecommendations(section);
  });
};

const initRecommendations = async (section) => {
  if (section.querySelector('.ze-related__track')) return;
  const params = new URLSearchParams({
    section_id: section.dataset.sectionId,
    product_id: section.dataset.productId,
    limit: section.dataset.limit || '6',
    intent: section.dataset.intent || 'related'
  });
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}recommendations/products?${params.toString()}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const replacement = doc.querySelector(`[data-ze-recommendations][data-section-id="${section.dataset.sectionId}"]`);
    if (replacement?.querySelector('.ze-related__track')) section.replaceWith(replacement);
    else section.remove();
  } catch { section.remove(); }
};

initZoomEyewear();
document.addEventListener('shopify:section:load', (event) => initZoomEyewear(event.target));

document.addEventListener('click', (event) => {
  const prev = event.target.closest('[data-ze-related-prev]');
  const next = event.target.closest('[data-ze-related-next]');
  const button = prev || next;
  if (!button) return;
  const track = button.closest('.ze-related')?.querySelector('[data-ze-related-track]');
  if (!track) return;
  track.scrollBy({ left: (prev ? -1 : 1) * track.clientWidth * .8 * (document.documentElement.dir === 'rtl' ? -1 : 1), behavior: 'smooth' });
});
